import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router";
import {
  ArchiveRestore,
  Copy,
  Download,
  Loader2,
  Pencil,
  Plus,
  RotateCcw,
  Search,
  Settings2,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { hasPermission, useClaims } from "@/hooks/use-claims";
import type { TFunction } from "@/i18n/catalog";
import { useTranslation } from "@/i18n/use-translation";
import { ApiRequestError } from "@/lib/api-error";
import { useAdminOrgStore } from "@/stores/admin-org";

import { COURSE_LABELS, courseOf } from "./course";
import { BackLink, CostCell, LoadError, PickASite, RowsSkeleton, useOpen } from "./menu-parts";
import {
  CADENCE_LABELS,
  costWarning,
  datesLabel,
  hasEnded,
  isReading,
  menuLabel,
  runsAt,
  serviceTime,
  uploadLine,
  type Menu,
  type MenuPart,
  type MenuDetail,
  type MenuRecipe,
} from "./menu-model";
import { MenuSettingsDialog } from "./menu-settings-dialog";
import { SetUpDialog, needsSetUp } from "./set-up-dialog";
import { stillReading } from "./upload-flow";
import {
  useMenuActions,
  useMenuDetail,
  useSiteCurrency,
  useSiteMenus,
  useSiteToday,
} from "./use-menus";

/**
 * One menu's recipes at the site in view (POS2-1456): rename, Re-extract, settings, and taking
 * recipes off the menu one at a time or several at once. An archived menu opens read-only from the
 * archive, so its recipes can be seen before it is restored; restoring is its one action.
 */

const PAGE = 8;

type Confirm = { title: string; body: string; action: string; onConfirm: () => void };

export default function MenuDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const siteId = useAdminOrgStore((s) => s.siteId);
  const siteName = useAdminOrgStore((s) => s.siteName);
  const [params] = useSearchParams();
  const fromArchive = params.get("from") === "archive";
  return (
    <div className="mx-auto max-w-5xl">
      {fromArchive ? (
        <BackLink label={t("menus.archived.title")} to="/menus/archive" />
      ) : (
        <BackLink label={t("menus.title")} to="/menus" />
      )}
      {siteId ? (
        <SiteMenuDetail key={`${id}:${siteId}`} menuId={id!} siteId={siteId} siteName={siteName} />
      ) : (
        <PickASite />
      )}
    </div>
  );
}

function SiteMenuDetail({
  menuId,
  siteId,
  siteName,
}: {
  menuId: string;
  siteId: string;
  siteName: string;
}) {
  const { t, locale } = useTranslation();
  const navigate = useNavigate();
  const openPage = useOpen();
  const { isPlatformAdmin, permissions } = useClaims();
  const canEdit = isPlatformAdmin || hasPermission(permissions, "menu:update");
  const canReread = isPlatformAdmin || hasPermission(permissions, "menu:create");
  const canAdd = isPlatformAdmin || hasPermission(permissions, "recipe:create");
  // Once this page has seen a read (or started one with Re-extract, or the archive's Set up and
  // restore did), poll through the brief "uploaded" and "extracted" steps until it ends. A menu
  // idle at either step is never polled.
  const location = useLocation();
  const [watching, setWatching] = useState(
    () => (location.state as { reading?: boolean } | null)?.reading === true,
  );
  const detail = useMenuDetail(
    menuId,
    siteId,
    (m) =>
      isReading(m) ||
      (watching && stillReading(m)) ||
      m.parts.some((part) => part.status === "reading"),
  );
  if (detail.data && isReading(detail.data) && !watching) setWatching(true);
  const menusQuery = useSiteMenus(siteId, false);
  const currency = useSiteCurrency(siteId);
  const today = useSiteToday(siteId);
  const { rename, reread, archiveWithUndo, removePart, restore, download } = useMenuActions();
  const [renaming, setRenaming] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settingUp, setSettingUp] = useState(false);
  const [confirm, setConfirm] = useState<Confirm | null>(null);

  if (detail.isPending || menusQuery.isPending || today === null) return <DetailSkeleton />;
  if (detail.isError) {
    if (detail.error instanceof ApiRequestError && detail.error.status === 404) {
      return (
        <p className="rounded-lg border border-black/[0.06] bg-white px-6 py-14 text-center text-sm text-gray-500">
          {t("menus.detail.notOnSite", { site: siteName })}
        </p>
      );
    }
    return <LoadError onRetry={() => detail.refetch()} />;
  }
  if (menusQuery.isError) return <LoadError onRetry={() => menusQuery.refetch()} />;

  const menu = detail.data;
  const menus = menusQuery.data;
  const live = runsAt(menu, siteId, today);
  const label = menuLabel(t, locale, menu);
  const reading = reread.isPending || isReading(menu) || (watching && stillReading(menu));
  const archived = menu.archived_at !== null;
  const edit = canEdit && !archived;
  // A seeded starter menu (POS2-731) was copied, not read from a file: there is nothing to re-read.
  const showReread = canReread && !archived && menu.file_type !== "seed";
  const state = archived ? "archived" : hasEnded(menu, today) ? "ended" : live ? "live" : "off";
  // A part that read dishes onto a live menu asks first, as removing recipes does.
  const removePartOf = (part: MenuPart) => {
    const go = () => removePart.mutate({ menuId, partId: part.id });
    if (part.status !== "read" || !live) return go();
    setConfirm({
      title: t("menus.detail.removePartTitle", { file: part.file_name }),
      body: t("menus.detail.removePartBody", { menu: label }),
      action: t("menus.detail.remove"),
      onConfirm: go,
    });
  };
  const meta = [
    t(CADENCE_LABELS[menu.cadence]),
    datesLabel(locale, menu),
    serviceTime(menu) ?? t("menus.detail.allDay"),
    menu.catalogue_name,
  ]
    .filter(Boolean)
    .join(" · ");

  function askReread() {
    const go = () => reread.mutate(menuId, { onSuccess: () => setWatching(true) });
    if (menu.recipes.length === 0) return go();
    // Re-extract reads the parts again too, and replaces what was changed by hand (D95); the waste
    // logged against a dish moves to its new recipe (D96).
    const changed = menu.recipes.filter((r) => r.changed_by_hand).map((r) => r.name);
    const shown = changed.slice(0, 3).join(", ");
    const names =
      changed.length > 3
        ? `${shown} ${t("menus.recipes.more", { count: changed.length - 3 })}`
        : shown;
    const read =
      menu.parts.length > 0
        ? t("menus.detail.rereadBodyParts", {
            file: menu.file_name,
            parts: menu.parts.map((part) => part.file_name).join(", "),
          })
        : t("menus.detail.rereadBody", { file: menu.file_name });
    setConfirm({
      title: t("menus.detail.rereadTitle", { name: label }),
      body: [
        read,
        changed.length > 0 && t("menus.detail.rereadChanged", { count: changed.length, names }),
        t("menus.detail.rereadKeepsWaste"),
      ]
        .filter(Boolean)
        .join(" "),
      action: t("menus.reExtract"),
      onConfirm: go,
    });
  }

  return (
    <>
      <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            {renaming ? (
              <TitleEditor
                value={label}
                onSave={(name) =>
                  rename.mutate(
                    { menuId, displayName: name },
                    { onSuccess: () => toast.success(t("menus.detail.nameSaved")) },
                  )
                }
                onDone={() => setRenaming(false)}
              />
            ) : (
              <>
                <h1 className="truncate text-lg font-medium text-gray-900">{label}</h1>
                {edit && (
                  <button
                    type="button"
                    aria-label={t("menus.detail.rename")}
                    onClick={() => setRenaming(true)}
                    className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                  >
                    <Pencil className="size-3.5" />
                  </button>
                )}
              </>
            )}
            <StatePill state={state} />
          </div>
          <p className="mt-0.5 text-sm text-gray-500">
            {meta}
            {edit && (
              <button
                type="button"
                disabled={reading}
                onClick={() => setSettingsOpen(true)}
                className="ml-2 text-gray-700 underline-offset-2 hover:underline disabled:text-gray-400 disabled:no-underline"
              >
                {t("menus.detail.edit")}
              </button>
            )}
          </p>
          <p className="text-xs text-gray-400">{uploadLine(t, locale, menu)}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {/* For debugging a read, so for everyone who can open the menu (POS2-1470). */}
          {menu.file_type !== "seed" && (
            <Button
              variant="outline"
              size="sm"
              disabled={download.isPending}
              onClick={() => download.mutate(menu)}
            >
              <Download />
              {t("menus.detail.download")}
            </Button>
          )}
          {showReread && (
            <Button variant="outline" size="sm" disabled={reading} onClick={askReread}>
              <RotateCcw />
              {t("menus.reExtract")}
            </Button>
          )}
          {/* A read places the dishes on the schedule it started with (D93). */}
          {edit && (
            <Button
              variant="outline"
              size="sm"
              disabled={reading}
              onClick={() => setSettingsOpen(true)}
            >
              <Settings2 />
              {t("menus.detail.settings")}
            </Button>
          )}
          {canAdd && !archived && (
            <Button size="sm" onClick={() => openPage(`/menus/recipes/new?menu=${menuId}`, label)}>
              <Plus />
              {t("menus.recipe.addTitle")}
            </Button>
          )}
          {archived &&
            canReread &&
            (needsSetUp(menu) ? (
              <Button size="sm" onClick={() => setSettingUp(true)}>
                <ArchiveRestore />
                {t("menus.archived.setUp")}
              </Button>
            ) : (
              <Button
                size="sm"
                disabled={restore.isPending}
                onClick={() =>
                  restore.mutate(menuId, {
                    onSuccess: () => toast.success(t("menus.archived.restored", { name: label })),
                  })
                }
              >
                <ArchiveRestore />
                {t("menus.restore")}
              </Button>
            ))}
        </div>
      </div>

      {reading ? (
        <div className="flex flex-col items-center rounded-lg border border-black/[0.06] bg-white px-6 py-16 text-center">
          <Loader2 className="size-6 animate-spin text-gray-400" />
          <p className="mt-3 text-sm text-gray-500">
            {t("menus.list.reading", { file: menu.file_name })}
          </p>
        </div>
      ) : (
        <>
          {menu.status === "failed" && (
            <p className="mb-3 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
              {menu.error_message ?? t("menus.detail.readFailed")}
            </p>
          )}
          {menu.cut_off_after && (
            <p className="mb-3 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800">
              {t("menus.detail.cutOff", { file: menu.file_name, dish: menu.cut_off_after })}
              {menu.cadence !== "always_on" && ` ${t("menus.detail.cutOffHint")}`}
            </p>
          )}
          {menu.parts.map((part) => (
            <p
              key={part.id}
              className={`mb-3 flex items-center justify-between gap-3 rounded-md px-3 py-2 text-sm ${
                part.status === "failed"
                  ? "bg-red-50 text-red-700"
                  : part.status === "reading" || part.cut_off_after
                    ? "bg-amber-50 text-amber-800"
                    : "bg-gray-50 text-gray-700"
              }`}
            >
              <span className="flex items-center gap-2">
                {part.status === "reading" && <Loader2 className="size-4 animate-spin" />}
                {partText(t, part)}
              </span>
              {edit && part.status !== "reading" && (
                <button
                  type="button"
                  className="shrink-0 font-medium underline"
                  onClick={() => removePartOf(part)}
                >
                  {t("menus.detail.removePart")}
                </button>
              )}
            </p>
          ))}
          {menu.recipes.length === 0 ? (
            <div className="flex flex-col items-center rounded-lg border border-black/[0.06] bg-white px-6 py-16 text-center">
              <p className="text-sm font-medium text-gray-900">{t("menus.detail.emptyTitle")}</p>
              {showReread && (
                <p className="mt-1 text-sm text-gray-500">
                  {t("menus.detail.emptyHint", { file: menu.file_name })}
                </p>
              )}
            </div>
          ) : (
            <RecipeTable
              menu={menu}
              menus={menus}
              live={live}
              canEdit={edit}
              currency={currency}
              onConfirm={setConfirm}
            />
          )}
        </>
      )}

      {settingsOpen && (
        <MenuSettingsDialog
          menu={menu}
          menus={menus}
          live={live}
          today={today}
          onClose={() => setSettingsOpen(false)}
          onArchive={() => {
            setSettingsOpen(false);
            archiveWithUndo(menuId, label, live, () => navigate("/menus"));
          }}
        />
      )}
      {settingUp && (
        <SetUpDialog
          menu={menu}
          hasRecipes={menu.recipes.length > 0}
          menus={menus}
          siteId={siteId}
          today={today}
          onClose={() => setSettingUp(false)}
          onRestored={(reading) => {
            setSettingUp(false);
            setWatching(reading);
          }}
        />
      )}
      {confirm && <ConfirmDialog confirm={confirm} onClose={() => setConfirm(null)} />}
    </>
  );
}

/** The title as an input: Enter or leaving the field saves, Esc puts the old name back. */
function TitleEditor({
  value,
  onSave,
  onDone,
}: {
  value: string;
  onSave: (name: string) => void;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState(value);
  const cancelled = useRef(false);
  const input = useRef<HTMLInputElement>(null);
  // Opened by the pencil: the name is ready to type over.
  useEffect(() => {
    input.current!.focus();
    input.current!.select();
  }, []);
  return (
    <Input
      ref={input}
      aria-label={t("menus.detail.nameLabel")}
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === "Escape") cancelled.current = true;
        if (e.key === "Enter" || e.key === "Escape") e.currentTarget.blur();
      }}
      onBlur={() => {
        const next = draft.trim();
        if (!cancelled.current && next && next !== value) onSave(next);
        onDone();
      }}
      className="h-8 w-72 max-w-full text-lg font-medium"
    />
  );
}

function StatePill({ state }: { state: "live" | "off" | "ended" | "archived" }) {
  const { t } = useTranslation();
  if (state === "live") {
    return (
      <span className="flex shrink-0 items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700">
        <span className="size-1.5 rounded-full bg-emerald-500" />
        {t("menus.detail.live")}
      </span>
    );
  }
  return (
    <span className="shrink-0 rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-500">
      {state === "ended"
        ? t("menus.groups.ended")
        : state === "archived"
          ? t("menus.detail.archived")
          : t("menus.detail.off")}
    </span>
  );
}

function RecipeTable({
  menu,
  menus,
  live,
  canEdit,
  currency,
  onConfirm,
}: {
  menu: MenuDetail;
  /** The site's menus, to name the one a dish is also live in. */
  menus: Menu[];
  live: boolean;
  canEdit: boolean;
  currency: string;
  onConfirm: (confirm: Confirm) => void;
}) {
  const { t, locale } = useTranslation();
  const openPage = useOpen();
  const { removeRecipes } = useMenuActions();
  const [query, setQuery] = useState("");
  const [showAll, setShowAll] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const name = menuLabel(t, locale, menu);

  const needle = query.trim().toLowerCase();
  const filtered = menu.recipes.filter((r) => r.name.toLowerCase().includes(needle));
  const visible = showAll || needle ? filtered : filtered.slice(0, PAGE);
  const allChecked = visible.length > 0 && visible.every((r) => selected.has(r.id));
  const someChecked = visible.some((r) => selected.has(r.id));
  const th = "text-xs uppercase tracking-wide text-gray-400";

  /** Off this menu only; a live menu asks first, since Scrappy stops matching the dish at once. */
  function remove(ids: string[], what: string) {
    const go = () =>
      removeRecipes.mutate(
        { menuId: menu.id, recipeIds: ids },
        {
          onSuccess: () => {
            setSelected((s) => new Set([...s].filter((id) => !ids.includes(id))));
            toast.success(t("menus.detail.removed", { name: what, menu: name }));
          },
        },
      );
    if (!live) return go();
    onConfirm({
      title: t("menus.detail.removeTitle", { name: what }),
      body: t("menus.detail.removeBody", { menu: name }),
      action: t("menus.detail.remove"),
      onConfirm: go,
    });
  }

  function toggle(id: string) {
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const open = (recipe: MenuRecipe) =>
    openPage(`/menus/recipes/${recipe.id}?from=${menu.id}`, name);

  return (
    <>
      <div className="mb-3 flex h-9 items-center gap-3">
        {selected.size > 0 ? (
          <div className="flex h-9 flex-1 items-center gap-3 rounded-md bg-gray-900 px-3 text-sm text-white">
            <span className="font-medium">
              {t("menus.detail.selected", { count: selected.size })}
            </span>
            <button
              type="button"
              disabled={removeRecipes.isPending}
              className="flex items-center gap-1.5 rounded px-2 py-1 hover:bg-white/10"
              onClick={() =>
                remove([...selected], t("menus.detail.recipeCount", { count: selected.size }))
              }
            >
              <Trash2 className="size-3.5" />
              {t("menus.detail.removeFromMenu")}
            </button>
            <button
              type="button"
              className="ml-auto rounded px-2 py-1 text-gray-300 hover:bg-white/10"
              onClick={() => setSelected(new Set())}
            >
              {t("menus.detail.clear")}
            </button>
          </div>
        ) : (
          <>
            <div className="relative flex-1">
              <Search className="absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-gray-300" />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t("menus.detail.search")}
                aria-label={t("menus.detail.search")}
                className="h-9 pl-9"
              />
            </div>
            <span className="shrink-0 text-sm text-gray-500">
              {t("menus.detail.recipeCount", { count: menu.recipes.length })}
            </span>
          </>
        )}
      </div>

      <div className="overflow-hidden rounded-lg border border-black/[0.06] bg-white">
        <Table>
          <TableHeader>
            <TableRow>
              {canEdit && (
                <TableHead className="w-10 pl-4">
                  <Checkbox
                    aria-label={t("menus.detail.selectAll")}
                    checked={allChecked ? true : someChecked ? "indeterminate" : false}
                    onCheckedChange={() =>
                      setSelected(allChecked ? new Set() : new Set(visible.map((r) => r.id)))
                    }
                  />
                </TableHead>
              )}
              <TableHead className={canEdit ? th : `pl-4 ${th}`}>
                {t("menus.columnRecipe")}
              </TableHead>
              <TableHead className={`hidden w-32 sm:table-cell ${th}`}>
                {t("menus.detail.columnCourse")}
              </TableHead>
              <TableHead className={`w-20 pr-4 text-right sm:w-28 sm:pr-2 ${th}`}>
                {t("menus.columnCostPerPortion")}
              </TableHead>
              <TableHead className={`hidden w-24 text-right sm:table-cell ${th}`}>
                {t("menus.detail.columnServings")}
              </TableHead>
              {canEdit && <TableHead className="hidden w-20 sm:table-cell" />}
            </TableRow>
          </TableHeader>
          <TableBody>
            {visible.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="py-10 text-center text-sm text-gray-500">
                  {t("menus.detail.noResults", { query: query.trim() })}
                </TableCell>
              </TableRow>
            )}
            {visible.map((recipe) => {
              const checked = selected.has(recipe.id);
              const course = courseOf(recipe.category_slug);
              const courseLabel = course ? t(COURSE_LABELS[course]) : null;
              const covers = recipe.planned_covers;
              const mobileLine = [
                courseLabel,
                covers === null ? null : t("menus.detail.servings", { count: covers }),
              ]
                .filter(Boolean)
                .join(" · ");
              return (
                <TableRow
                  key={recipe.id}
                  onClick={() => open(recipe)}
                  className={`cursor-pointer ${checked ? "bg-blue-50" : "hover:bg-black/[0.02]"}`}
                >
                  {canEdit && (
                    <TableCell className="pl-4" onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={checked}
                        aria-label={t("menus.detail.select", { name: recipe.name })}
                        onCheckedChange={() => toggle(recipe.id)}
                      />
                    </TableCell>
                  )}
                  <TableCell className={`whitespace-normal py-2.5 ${canEdit ? "" : "pl-4"}`}>
                    <p className="text-sm font-medium text-gray-900">{recipe.name}</p>
                    <RowFlag recipe={recipe} menus={menus} />
                    {mobileLine && <p className="text-xs text-gray-400 sm:hidden">{mobileLine}</p>}
                  </TableCell>
                  <TableCell className="hidden text-sm text-gray-500 sm:table-cell">
                    {courseLabel ?? "-"}
                  </TableCell>
                  <TableCell className="pr-4 text-right sm:pr-2">
                    <CostCell
                      perPortion={recipe.cost_per_portion}
                      weightKg={recipe.portion_weight_kg}
                      currency={currency}
                    />
                  </TableCell>
                  <TableCell
                    className={`hidden text-right text-sm tabular-nums sm:table-cell ${
                      covers === null ? "text-gray-300" : "text-gray-900"
                    }`}
                  >
                    {covers ?? "-"}
                  </TableCell>
                  {canEdit && (
                    <TableCell
                      className="hidden pr-4 sm:table-cell"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="flex justify-end gap-1">
                        <button
                          type="button"
                          aria-label={t("menus.detail.editRecipe", { name: recipe.name })}
                          className="rounded-md p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                          onClick={() => open(recipe)}
                        >
                          <Pencil className="size-3.5" />
                        </button>
                        <button
                          type="button"
                          aria-label={t("menus.detail.removeRecipe", { name: recipe.name })}
                          disabled={removeRecipes.isPending}
                          className="rounded-md p-1.5 text-gray-400 hover:bg-gray-100 hover:text-red-600"
                          onClick={() => remove([recipe.id], recipe.name)}
                        >
                          <Trash2 className="size-3.5" />
                        </button>
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        {!needle && filtered.length > PAGE && (
          <div className="flex items-center justify-between border-t border-black/[0.06] px-4 py-2.5 text-sm">
            <span className="text-gray-500">
              {t("menus.detail.showing", { shown: visible.length, total: filtered.length })}
            </span>
            <button
              type="button"
              className="font-medium text-gray-700 hover:text-gray-900"
              onClick={() => setShowAll(!showAll)}
            >
              {showAll ? t("menus.detail.showFewer") : t("menus.detail.showAll")}
            </button>
          </div>
        )}
      </div>
    </>
  );
}

/** One flag per row: a missing or estimated cost, else the dish live on another menu, else matched. */
function RowFlag({ recipe, menus }: { recipe: MenuRecipe; menus: Menu[] }) {
  const { t, locale } = useTranslation();
  const warning = costWarning(t, recipe);
  if (warning) {
    return <p className="text-xs text-amber-700">{warning}</p>;
  }
  // The site's list is a separate read: a menu archived in between is simply not named.
  const other = menus.find((m) => m.id === recipe.also_live_in);
  if (other) {
    return (
      <p className="flex items-center gap-1 text-xs text-amber-700">
        <Copy className="size-3 shrink-0" />
        {t("menus.detail.alsoLive", { menu: menuLabel(t, locale, other) })}
      </p>
    );
  }
  return <p className="text-xs text-gray-400">{t("menus.detail.matched")}</p>;
}

function partText(t: TFunction, part: MenuPart): string {
  switch (part.status) {
    case "reading":
      return t("menus.detail.partReading", { file: part.file_name });
    case "failed":
      return t("menus.detail.partFailed", { file: part.file_name });
    case "read":
      return part.cut_off_after
        ? t("menus.detail.cutOff", { file: part.file_name, dish: part.cut_off_after })
        : t("menus.detail.partRead", { file: part.file_name });
  }
}

function ConfirmDialog({ confirm, onClose }: { confirm: Confirm; onClose: () => void }) {
  const { t } = useTranslation();
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{confirm.title}</DialogTitle>
          <DialogDescription>{confirm.body}</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button
            variant="destructive"
            size="sm"
            onClick={() => {
              confirm.onConfirm();
              onClose();
            }}
          >
            {confirm.action}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DetailSkeleton() {
  return (
    <>
      <div className="mb-5 space-y-2">
        <Skeleton className="h-6 w-64 max-w-full" />
        <Skeleton className="h-4 w-80 max-w-full" />
        <Skeleton className="h-3 w-72 max-w-full" />
      </div>
      <RowsSkeleton />
    </>
  );
}
